import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateAdminUser } from '../../api/admin';

/* Réinitialisation du mot de passe d'un compte, par l'administrateur Palestre.

   Le serveur acceptait déjà le changement (PATCH /api/admin/users/{id}) ; aucun
   écran ne l'appelait. Sans cela, un utilisateur qui perd son mot de passe n'a
   AUCUN recours : il n'existe ni « mot de passe oublié » ni écran de changement
   côté utilisateur.

   Le mot de passe n'est jamais relisible — seule une empreinte est stockée. On
   ne peut donc que le remplacer, et l'afficher UNE fois pour le transmettre.
   L'œil sert à relire ce qu'on vient de taper, pas à révéler un secret stocké. */

// Alphabet sans caractères ambigus (0/O, 1/l/I) : un mot de passe temporaire se
// transmet souvent à l'oral ou se recopie à la main.
const ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function generatePassword(length = 14) {
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (v) => ALPHABET[v % ALPHABET.length]).join('');
}

export default function PasswordResetRow({ user, colSpan, onDone, addToast }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = value.length > 0 && value.length < 8;

  async function save() {
    if (value.length < 8) return;
    setBusy(true);
    const res = await updateAdminUser(user.id, { password: value });
    setBusy(false);
    if (res.error || !res.data) {
      addToast?.(res.error || t('admin:users.password.failed'), 'error');
      return;
    }
    addToast?.(t('admin:users.password.saved', { name: user.name || user.email }));
    onDone?.();
  }

  return (
    <tr className="admin-pwd-row">
      <td colSpan={colSpan}>
        <div className="admin-pwd">
          <span className="admin-pwd__lbl">
            {t('admin:users.password.title', { email: user.email })}
          </span>
          <span className="admin-pwd__field">
            <input
              type={reveal ? 'text' : 'password'}
              value={value}
              autoComplete="new-password"
              placeholder={t('admin:users.password.placeholder')}
              onChange={(e) => setValue(e.target.value)}
            />
            <button
              type="button"
              className="admin-pwd__eye"
              onClick={() => setReveal((v) => !v)}
              title={reveal ? t('admin:users.password.hide') : t('admin:users.password.show')}
            >
              {reveal ? '🙈' : '👁'}
            </button>
          </span>
          <button
            type="button"
            className="admin-btn-ghost"
            onClick={() => { setValue(generatePassword()); setReveal(true); }}
          >
            {t('admin:users.password.generate')}
          </button>
          <button
            type="button"
            className="admin-btn"
            disabled={busy || value.length < 8}
            onClick={save}
          >
            {t('admin:common.save')}
          </button>
          <button type="button" className="admin-btn-ghost" onClick={onDone}>
            {t('common:actions.cancel', 'Annuler')}
          </button>
          {tooShort && (
            <span className="admin-pwd__hint">{t('admin:users.password.tooShort')}</span>
          )}
        </div>
        <p className="admin-pwd__note">{t('admin:users.password.note')}</p>
      </td>
    </tr>
  );
}
